import React, { useCallback, useEffect, useMemo, useState } from 'react';

async function readJson(res) {
  try {
    return await res.json();
  } catch (_e) {
    return {};
  }
}

export default function FriendsModal({
  open,
  onClose,
  onOpenProfile,
  currentUserId,
  friends = [],
  incoming = [],
  outgoing = [],
  friendListsError = null,
  onReloadFriendLists,
}) {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState('idle');
  const [searchError, setSearchError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const refreshLists = useCallback(async () => {
    if (onReloadFriendLists) {
      await onReloadFriendLists();
    }
  }, [onReloadFriendLists]);

  const openProfile = useCallback(
    (uid) => {
      if (!uid || typeof onOpenProfile !== 'function') return;
      // Navigate first (avoid showing the underlying list between close + route change),
      // then close the modal on the next frame so the backdrop stays up and prevents a flash.
      onOpenProfile(uid);
      if (onClose) {
        requestAnimationFrame(() => requestAnimationFrame(() => onClose()));
      }
    },
    [onOpenProfile, onClose],
  );

  function canClickProfile(u, rel) {
    if (!u?.id || !u?.username) return false;
    if (currentUserId && String(u.id) === String(currentUserId)) return true;
    const vis = u.profile_visibility || u.profileVisibility || 'public';
    if (vis === 'private') return false;
    if (vis === 'friends_only' && rel !== 'friends') return false;
    if (rel === 'friends') return true;
    return true;
  }

  useEffect(() => {
    if (!open) {
      setSearchText('');
      setSearchResults([]);
      setSearchStatus('idle');
      setSearchError(null);
      setActionError(null);
      setSearchFocused(false);
    }
  }, [open]);

  const friendIdSet = useMemo(
    () => new Set((friends || []).map((x) => x.user?.id).filter(Boolean)),
    [friends],
  );
  const incomingRequesterSet = useMemo(
    () => new Set((incoming || []).map((x) => x.requester?.id).filter(Boolean)),
    [incoming],
  );
  const outgoingAddresseeSet = useMemo(
    () => new Set((outgoing || []).map((x) => x.addressee?.id).filter(Boolean)),
    [outgoing],
  );

  useEffect(() => {
    if (!open) return;
    const q = searchText.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchStatus('idle');
      setSearchError(null);
      return;
    }
    let cancelled = false;
    setSearchStatus('loading');
    setSearchError(null);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/users/search?q=${encodeURIComponent(q)}`,
          { credentials: 'include' },
        );
        const body = await readJson(res);
        if (cancelled) return;
        if (!res.ok || !body.ok) {
          setSearchStatus('error');
          setSearchError(body.error || 'Search failed');
          setSearchResults([]);
          return;
        }
        setSearchResults(body.results || []);
        setSearchStatus('idle');
      } catch (err) {
        if (cancelled) return;
        setSearchStatus('error');
        setSearchError(String(err));
        setSearchResults([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchText, open]);

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await readJson(res);
    return { res, data };
  }

  const handleAccept = async (requesterId) => {
    if (!requesterId || busyId) return;
    setBusyId(requesterId);
    try {
      const { res, data } = await postJson('/api/friends/accept', {
        requesterId,
      });
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Accept failed');
      }
      await refreshLists();
      setSearchText('');
      setSearchResults([]);
    } catch (err) {
      console.error(err);
      setActionError((err && err.message) || 'Accept failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (requesterId) => {
    if (!requesterId || busyId) return;
    setBusyId(requesterId);
    try {
      const { res, data } = await postJson('/api/friends/decline', {
        requesterId,
      });
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Decline failed');
      }
      await refreshLists();
    } catch (err) {
      console.error(err);
      setActionError((err && err.message) || 'Decline failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!friendId || busyId) return;
    setBusyId(friendId);
    try {
      const { res, data } = await postJson('/api/friends/remove', {
        friendId,
      });
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Could not remove friend');
      }
      await refreshLists();
    } catch (err) {
      console.error(err);
      setActionError((err && err.message) || 'Could not remove friend');
    } finally {
      setBusyId(null);
    }
  };

  const handleRequest = async (userId) => {
    if (!userId || busyId) return;
    setBusyId(userId);
    try {
      const { res, data } = await postJson('/api/friends/request', {
        userId,
      });
      if (res.status === 409 && data.code === 'incoming_pending') {
        await refreshLists();
        setBusyId(null);
        return;
      }
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Request failed');
      }
      await refreshLists();
    } catch (err) {
      console.error(err);
      setActionError((err && err.message) || 'Request failed');
    } finally {
      setBusyId(null);
    }
  };

  function relationForUserId(uid) {
    if (!uid || uid === currentUserId) return 'self';
    if (friendIdSet.has(uid)) return 'friends';
    if (incomingRequesterSet.has(uid)) return 'incoming';
    if (outgoingAddresseeSet.has(uid)) return 'outgoing';
    return 'none';
  }

  const visibleSearchResults = useMemo(
    () =>
      searchResults.filter(
        (u) => u?.id && String(u.id) !== String(currentUserId),
      ),
    [searchResults, currentUserId],
  );

  const showSearchDropdown =
    searchFocused &&
    (searchText.trim().length >= 2 || searchStatus === 'loading');

  if (!open) return null;

  return (
    <div
      className="flique-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flique-friends-modal-title"
      onClick={onClose}
    >
      <div
        className="flique-modal card flique-card flique-friends-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-body flique-friends-modal-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 id="flique-friends-modal-title" className="h5 mb-0">
              Friends
            </h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-light flique-modal-close"
              aria-label="Close"
              onClick={onClose}
            />
          </div>

          <div
            className={`flique-friends-search-wrap mb-3${showSearchDropdown ? ' flique-friends-search-wrap--open' : ''}`}
          >
            <div className="flique-friends-search-input-shell">
              <label
                className="form-label visually-hidden"
                htmlFor="flique-friends-search"
              >
                Search people
              </label>
              <input
                id="flique-friends-search"
                type="search"
                className="form-control"
                placeholder="Search people by username…"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                aria-autocomplete="list"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
            </div>
            {showSearchDropdown && (
              <div
                id="flique-friends-search-dropdown"
                className="flique-friends-search-dropdown"
                role="listbox"
                aria-label="Matching users"
                aria-busy={searchStatus === 'loading'}
                onMouseDown={(e) => e.preventDefault()}
              >
                {searchStatus === 'error' && (
                  <p className="small text-danger mb-0 flique-friends-dropdown-pad">
                    {searchError}
                  </p>
                )}
                {searchStatus === 'loading' && (
                  <div
                    className="flique-friends-dropdown-loading"
                    aria-hidden="true"
                  />
                )}
                {searchStatus === 'idle' && visibleSearchResults.length === 0 && (
                  <p className="small text-muted mb-0 flique-friends-dropdown-pad flique-placeholder-muted">
                    No users found.
                  </p>
                )}
                {searchStatus === 'idle' && visibleSearchResults.length > 0 && (
                  <ul className="list-unstyled mb-0 flique-friends-dropdown-list">
                    {visibleSearchResults.map((u) => {
                      const rel = relationForUserId(u.id);
                      const clickable = canClickProfile(u, rel);
                      return (
                        <li
                          key={u.id}
                          className="flique-friends-dropdown-row"
                          role="option"
                          aria-selected="false"
                        >
                          <div className="flique-friends-row-main">
                            <span
                              className="flique-friends-avatar"
                              aria-hidden="true"
                            >
                              {(u.avatar_initials || u.username || '?')
                                .toString()
                                .slice(0, 2)
                                .toUpperCase()}
                            </span>
                            {clickable ? (
                              <button
                                type="button"
                                className="flique-userlink fw-semibold"
                                onClick={() => openProfile(u.id)}
                              >
                                @{u.username}
                              </button>
                            ) : (
                              <div className="fw-semibold">@{u.username}</div>
                            )}
                          </div>
                          <div className="flique-friends-row-actions">
                            {rel === 'friends' && (
                              <span className="small text-muted">Friends</span>
                            )}
                            {rel === 'incoming' && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-flique"
                                  disabled={busyId === u.id}
                                  onClick={() => handleAccept(u.id)}
                                >
                                  Accept
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-flique-secondary"
                                  disabled={busyId === u.id}
                                  onClick={() => handleDecline(u.id)}
                                >
                                  Decline
                                </button>
                              </>
                            )}
                            {rel === 'outgoing' && (
                              <span className="small text-muted">Sent</span>
                            )}
                            {rel === 'none' && (
                              <button
                                type="button"
                                className="btn btn-sm btn-flique-secondary"
                                disabled={busyId === u.id}
                                onClick={() => handleRequest(u.id)}
                              >
                                Request
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          {friendListsError && (
            <p className="small text-danger mb-2">{friendListsError}</p>
          )}
          {actionError && (
            <p className="small text-danger mb-2">{actionError}</p>
          )}

          <section
            className="flique-friends-panel mb-3"
            aria-label="Friend requests"
          >
            <h3 className="h6 text-muted mb-2 flique-friends-panel-title">
              Requests
            </h3>
            <div
              className={`flique-friends-list-scroll${
                incoming.length === 0
                  ? ' flique-friends-list-scroll--empty'
                  : ''
              }`}
            >
              {incoming.length === 0 ? (
                <p className="small text-muted mb-0 flique-friends-scroll-empty flique-placeholder-muted">
                  No pending requests.
                </p>
              ) : (
                <ul className="list-unstyled mb-0 flique-friends-list">
                  {incoming.map((row) => {
                    const u = row.requester;
                    if (!u) return null;
                    const clickable = canClickProfile(u, 'incoming');
                    return (
                      <li key={u.id} className="flique-friends-row">
                        <div className="flique-friends-row-main">
                          <span
                            className="flique-friends-avatar"
                            aria-hidden="true"
                          >
                            {(u.avatar_initials || u.username || '?')
                              .toString()
                              .slice(0, 2)
                              .toUpperCase()}
                          </span>
                          <div>
                            {clickable ? (
                              <button
                                type="button"
                                className="flique-userlink fw-semibold"
                                onClick={() => openProfile(u.id)}
                              >
                                @{u.username}
                              </button>
                            ) : (
                              <div className="fw-semibold">@{u.username}</div>
                            )}
                          </div>
                        </div>
                        <div className="flique-friends-row-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-flique"
                            disabled={busyId === u.id}
                            onClick={() => handleAccept(u.id)}
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-flique-secondary"
                            disabled={busyId === u.id}
                            onClick={() => handleDecline(u.id)}
                          >
                            Decline
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          <section className="flique-friends-panel" aria-label="Your friends">
            <h3 className="h6 text-muted mb-2 flique-friends-panel-title">
              Your friends
            </h3>
            <div
              className={`flique-friends-list-scroll${
                friends.length === 0 ? ' flique-friends-list-scroll--empty' : ''
              }`}
            >
              {friends.length === 0 ? (
                <p className="small text-muted mb-0 flique-friends-scroll-empty flique-placeholder-muted">
                  No friends yet. Find people with search or accept a request.
                </p>
              ) : (
                <ul className="list-unstyled mb-0 flique-friends-list">
                  {friends.map((row) => {
                    const u = row.user;
                    if (!u) return null;
                    const clickable = canClickProfile(u, 'friends');
                    return (
                      <li key={u.id} className="flique-friends-row">
                        <div className="flique-friends-row-main">
                          <span
                            className="flique-friends-avatar"
                            aria-hidden="true"
                          >
                            {(u.avatar_initials || u.username || '?')
                              .toString()
                              .slice(0, 2)
                              .toUpperCase()}
                          </span>
                          {clickable ? (
                            <button
                              type="button"
                              className="flique-userlink fw-semibold"
                              onClick={() => openProfile(u.id)}
                            >
                              @{u.username}
                            </button>
                          ) : (
                            <div className="fw-semibold">@{u.username}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="flique-icon-remove-x"
                          aria-label={`Remove @${u.username || 'user'} from friends`}
                          disabled={busyId === u.id}
                          onClick={() => handleRemoveFriend(u.id)}
                        >
                          ×
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
